export const AUTH_LOST_EVENT = 'sbg:auth-lost';

const REFRESH_PATH = '/api/auth/refresh';

const PATHS_THAT_NEVER_REFRESH: readonly string[] = [
  REFRESH_PATH,
  '/api/auth/login',
  '/api/auth/logout',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
];

export interface ApiFieldIssue {
  readonly path: string;
  readonly message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: readonly ApiFieldIssue[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  fieldMessage(path: string): string | undefined {
    return this.issues.find((issue) => issue.path === path)?.message;
  }
}

interface ErrorEnvelope {
  readonly error?: {
    readonly code?: string;
    readonly message?: string;
    readonly issues?: readonly ApiFieldIssue[];
    readonly requestId?: string;
  };
}

let refreshInFlight: Promise<boolean> | null = null;

async function runRefresh(): Promise<boolean> {
  try {
    const response = await fetch(REFRESH_PATH, { method: 'POST', credentials: 'include' });
    return response.ok;
  } catch {
    return false;
  }
}

function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= runRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

function announceAuthLost(): void {
  window.dispatchEvent(new CustomEvent(AUTH_LOST_EVENT));
}

async function toApiError(response: Response): Promise<ApiError> {
  let envelope: ErrorEnvelope = {};

  try {
    envelope = (await response.json()) as ErrorEnvelope;
  } catch {
    envelope = {};
  }

  const error = envelope.error;

  return new ApiError(
    response.status,
    error?.code ?? 'internal_error',
    error?.message ?? 'Something went wrong.',
    error?.issues ?? [],
    error?.requestId,
  );
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly formData?: FormData;
  readonly signal?: AbortSignal;
}

export async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const method = options.method ?? 'GET';

  const send = (): Promise<Response> =>
    fetch(path, {
      method,
      credentials: 'include',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      ...(options.formData === undefined ? {} : { body: options.formData }),
      ...(options.body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(options.body) }),
    });

  let response = await send();

  if (
    response.status === 401 &&
    !PATHS_THAT_NEVER_REFRESH.some((candidate) => path.startsWith(candidate))
  ) {
    const recovered = await refreshOnce();

    if (!recovered) {
      announceAuthLost();
      throw await toApiError(response);
    }

    response = await send();
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

export function buildQuery<TParams extends object>(params: TParams): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }

  const query = search.toString();
  return query.length === 0 ? '' : `?${query}`;
}
